// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { get } from 'lodash';
import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    ActionButton,
    IIconProps,
    PrimaryButton,
    Spinner,
    SpinnerSize,
} from 'office-ui-fabric-react';
import ErrorMessage from './../../components/ErrorMessage';
import { getUserTierConfig, checkIsUserEnterpriseCategoryFromTier } from './../../lib/userTierConfig';
import { statusSelector } from './../../components/ServiceRequestAndDeviceLookup/selector';
import { TranslationDetails, translateMessage } from './../../helpers/errorMessageInterpreter';
import {
    CHECKOUT_ORDER,
    GET_REGISTER_INFO_DATA,
    checkoutOrder,
    customerInfoSelector,
    reActivateOrderStatusSelector,
    PURCHASE_ON_DEMAND_ORDER,
    purchaseOnDemandOrder,
    offerReasonsSelector
} from './../../store/orderCreation';
import {
    deviceSelector,
} from './../../store/deviceInfo/selectors';
import { actionStatusReset, LoadingState } from './../../store/actionStatus';
import { userAgentTierSelector } from './../../store/user';

import Styles from './registrationDetail.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';

export default function RegisterDetail(props: any) {
    const { disabled } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const customerInfo = useSelector(state => customerInfoSelector(state));
    const deviceInfo = useSelector(state => deviceSelector(state));
    const offerReasons = useSelector(state => offerReasonsSelector(state));
    const repairReason = offerReasons?.repairReason;
    const repairComments = offerReasons?.repairComments;
    const isDoNotRepairSelected = !!repairReason;

    //to get the current tier information and check whether Enterprise category or not
    const userAgentTier = useSelector(state => userAgentTierSelector(state));
    const isEnterpriseCustomer = checkIsUserEnterpriseCategoryFromTier(userAgentTier);
    const reActivateOrder = useSelector(state => reActivateOrderStatusSelector(state));

    let checkoutOrderStatus = useSelector(state =>
        statusSelector(state)(CHECKOUT_ORDER)
    );

    if (reActivateOrder) {
        checkoutOrderStatus = useSelector(state =>
            statusSelector(state)(PURCHASE_ON_DEMAND_ORDER)
        );
    }

    const isLoading = checkoutOrderStatus.status === LoadingState.started;
    const checkoutOrderIsError = checkoutOrderStatus.status === LoadingState.rejected;

    const disableSubmit = disabled || isLoading || (isDoNotRepairSelected && (!repairReason || !repairComments));

    const onSubmit = () => {

        if (isDoNotRepairSelected && (!repairReason || !repairComments)) { alert('Please select a reason and enter comments for Do Not Repair.');
            return;
        }

        logPageAction('Continue to register device: continue button clicked');

        if (!reActivateOrder) {
            dispatch(checkoutOrder());
        } else {
            dispatch(purchaseOnDemandOrder({ isOnDemandCheckOut: true, dnrReason: repairReason, dnrComments: repairComments}));
        }
    };

    const editRegistration = () => {
        logPageAction('Shipping info edit button clicked');
        dispatch(actionStatusReset({ actionName: GET_REGISTER_INFO_DATA }));
    };
    const deviceName = deviceInfo && (get(deviceInfo, 'part.repairProduct.series') || get(deviceInfo, 'part.msPressSeriesName')) || '';   
    const parentheticalDeviceName = deviceName ? `(${deviceName})` : '';

    let checkoutOrderMessage = '';
    let displayOnlyOperationId = false;
    if (checkoutOrderStatus) {
        const checkoutOrderRequestErrorMessage = get(checkoutOrderStatus, 'error.data.description') || get(checkoutOrderStatus, 'error.data.ErrorMessage')
            || get(checkoutOrderStatus, 'error.data.message');

        const translatedCheckoutRequestErrorMessage: TranslationDetails | undefined = translateMessage(checkoutOrderRequestErrorMessage);
        if (translatedCheckoutRequestErrorMessage) {
            checkoutOrderMessage = t(translatedCheckoutRequestErrorMessage.key, translatedCheckoutRequestErrorMessage.values);
            if (translatedCheckoutRequestErrorMessage.key === 'fields_must_be_provided'
                && translatedCheckoutRequestErrorMessage.values.fields && translatedCheckoutRequestErrorMessage.values.fields.toUpperCase().includes('ADDRESSLINE1')) {
                // The user account's store could be misconfigured and they are receiving invalid address line 1 message
                checkoutOrderMessage = t('account_setup_issue_contact_account_admin');
                displayOnlyOperationId = false;
            }
            else if (['component_used_in_another_repair', 'component_used_in_another_repair_with_only_serialNumber'].includes(translatedCheckoutRequestErrorMessage?.key)) {
                displayOnlyOperationId = true;
            }
        } else if (checkoutOrderRequestErrorMessage) {
            // If no translation found but there was a service error picked up, set checkoutOrderMessage to that English service error message
            if (checkoutOrderRequestErrorMessage === t('invalid_character_contact_information') || checkoutOrderRequestErrorMessage === t('character_limit_comments')
                || checkoutOrderRequestErrorMessage === t('organiztion_contact_character_limit') || checkoutOrderRequestErrorMessage === t('invalid_email_address_characters')
                || checkoutOrderRequestErrorMessage === t('invalid_name_characters') || checkoutOrderRequestErrorMessage === t('invalid_postal_code_characters_length') || checkoutOrderRequestErrorMessage.match(t('invalid_bill_to_address_postal_code'))
                || checkoutOrderRequestErrorMessage.match(t('invalid_ship_to_address_postal_code')) || checkoutOrderRequestErrorMessage.match(t('invalid_shipping_region'))) {
                checkoutOrderMessage = checkoutOrderRequestErrorMessage;
                displayOnlyOperationId = true;
            } 
            else if (checkoutOrderRequestErrorMessage === t('invalid_payment_gateway', { lng: 'en' })) {
                checkoutOrderMessage = t('invalid_payment_gateway');
                displayOnlyOperationId = true;
            }
            else {
                checkoutOrderMessage = "";
                displayOnlyOperationId = false;
            }
        }

        if (!checkoutOrderMessage && checkoutOrderIsError) {
            // Check if custom logic throw error and saved message in local payload
            checkoutOrderMessage = t(get(checkoutOrderStatus, 'payload.message'), { deviceName: parentheticalDeviceName })
                || t(get(checkoutOrderStatus, 'message'))
                || t(get(checkoutOrderStatus, 'error.message'));
            displayOnlyOperationId = false;
        }
    }

    const editIcon: IIconProps = { iconName: 'edit' };
    return (
        <section id="registration">
            <div className={clsx('ms-Grid')}>
                {!reActivateOrder && (
                    <div className={clsx(Styles.registerDevice)}>
                        <div
                            className={clsx(
                                Styles.registerDeviceTitle,
                                'ms-Grid-row'
                            )}
                        >
                            <div className={clsx('ms-Grid-col', 'ms-sm6')}>
                                <div className={Styles.titleHeading}>
                                    <span>{t('register_device')}</span>

                                    <ActionButton
                                        ariaLabel={t('edit')}
                                        className={Styles.edit}
                                        iconProps={editIcon}
                                        onClick={editRegistration}
                                        text={t('edit')}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="ms-Grid-row">
                            <div className={clsx('ms-Grid-col', 'ms-sm3', Styles.marginBottom10)}>
                                <div>{isEnterpriseCustomer === true ? t('first_name_org_label') : t('first_name')}</div>
                            </div>
                            <div className={clsx('ms-Grid-col', 'ms-sm3', Styles.marginBottom10)}>
                                <div className={Styles.boldText}>
                                    {customerInfo.firstName}
                                </div>
                            </div>
                            <div className={clsx('ms-Grid-col', 'ms-sm3', Styles.marginBottom10)}>
                                <div>{isEnterpriseCustomer === true ? t('last_name_org_label') : t('last_name')}</div>
                            </div>
                            <div className={clsx('ms-Grid-col', 'ms-sm3', Styles.marginBottom10)}>
                                <div className={Styles.boldText}>
                                    {customerInfo.lastName}
                                </div>
                            </div>
                            {getUserTierConfig().commercialRegistration && isEnterpriseCustomer === false &&
                                <>
                                    <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                                        <div>{t('organization_name')}</div>
                                    </div>
                                    <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                                        <div className={Styles.boldText}>
                                            {customerInfo.orgName}
                                        </div>
                                    </div>
                                </>
                            }
                            <div className={clsx('ms-Grid-col', 'ms-sm3', Styles.marginBottom10)}>
                                <div>{t('email')}</div>
                            </div>
                            <div className={clsx('ms-Grid-col', 'ms-sm3', Styles.marginBottom10)}>
                                <div className={Styles.boldText}>
                                    {customerInfo.microsoftAccount}
                                </div>
                            </div>
                            <div className={clsx('ms-Grid-col', 'ms-sm3', Styles.marginBottom10)}>
                                <div>{t('phone_number')}</div>
                            </div>
                            <div className={clsx('ms-Grid-col', 'ms-sm3', Styles.marginBottom10)}>
                                <div className={Styles.boldText}>
                                    {customerInfo.phoneNumber}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="ms-Grid-row">
                    <div className={clsx('ms-Grid-col', 'ms-sm9')} />
                    <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                        <div className={Styles.submitAndSpinnerContainer}>
                            <PrimaryButton
                                allowDisabledFocus={true}
                                ariaLabel={t('continue')}
                                autoFocus={true}
                                className={Styles.submit}
                                disabled={disableSubmit}
                                onClick={onSubmit}
                                tabIndex={0}
                                text={t('continue')}
                            />
                            {isLoading && (
                                <Spinner
                                    size={SpinnerSize.medium}
                                    className={Styles.spinner}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <ErrorMessage
                    contactSupport={checkoutOrderIsError}
                    message={checkoutOrderIsError ? checkoutOrderMessage : undefined}
                    traceData={checkoutOrderStatus}
                    displayOnlyOperationId={displayOnlyOperationId}
                />
            </div>
        </section>
    );
}